/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCallAndPoll, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { OperationRetryStrategies } from 'src/core/utils/api/operationRetryStrategies';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import {
    ConversationInput,
    ConversationSummarizationSwagger,
    SummaryAspects
} from 'src/languageStudio/core/services/conversation/conversationSwaggers';
import { SummarizationJobStateSwagger } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

export const getConversationSummarizationPayloadTasks = (summaryAspects: SummaryAspects[], sentenceCount: number) => {
    const originalTaskAspects = summaryAspects.filter(aspect => aspect !== 'resolution');

    return [
        ...(originalTaskAspects.length
            ? [
                  {
                      taskName: 'originalTask',
                      kind: 'ConversationalSummarizationTask',
                      parameters: {
                          summaryAspects: originalTaskAspects,
                          labFeatures: {
                              flights: '2023-03-10-v2'
                          }
                      }
                  }
              ]
            : []),
        ...(summaryAspects.includes('resolution')
            ? [
                  {
                      taskName: 'resolutionTask',
                      kind: 'ConversationalSummarizationTask',
                      parameters: {
                          summaryAspects: ['resolution'],
                          sentenceCount,
                          labFeatures: {
                              flights: '2023-03-10-v2'
                          }
                      }
                  }
              ]
            : [])
    ];
};

/**
 * Represents a service for conversation operations like conversation summarization , conversation PII, ...
 */
export const getConversationAnalysisService = createService(({ context, getEnvironment }) => {
    const { apiUrl: languageTextAnalyticsBaseUrl } = getEnvironment({ apiName: 'languageTextAnalytics' });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, true)
        .build();

    const getUrlBuilder = (apiVersion?: string) =>
        urlBuilder()
            .baseUrl(languageTextAnalyticsBaseUrl)
            .resource('analyze-conversations')
            .resource('jobs')
            .queryParam('api-version', apiVersion || LuisConstants.Demos.ConversationApiVersion);

    /**
     * Gets a summary analysis for the input conversation.
     */
    const getConversationSummarization = async ({
        conversations,
        sentenceCount,
        summaryAspects,
        pollingCallHandler
    }: {
        sentenceCount?: number;
        summaryAspects: SummaryAspects[];
        conversations: ConversationInput[];
        pollingCallHandler?: OperationHandler<SummarizationJobStateSwagger<ConversationSummarizationSwagger>>['handleCall'];
    }): Promise<SummarizationJobStateSwagger<ConversationSummarizationSwagger>> => {
        const postUrl = getUrlBuilder().build();

        const response = await createOperationCallAndPoll<SummarizationJobStateSwagger<ConversationSummarizationSwagger>>(
            httpService.post<ConversationSummarizationSwagger>(
                postUrl,
                {
                    kind: 'Conversation',
                    analysisInput: { conversations },
                    tasks: getConversationSummarizationPayloadTasks(summaryAspects, sentenceCount)
                },
                apiOptions
            ),
            apiOptions,
            {
                handleCall: pollingCallHandler,
                retryStrategy: OperationRetryStrategies.defaultRetryStrategy,
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy
            },
            res => (<Record<string, string>>res.headers)[LuisConstants.OperationHeaders.LocationHeader]
        );

        return response.data;
    };

    return { getConversationSummarization };
});
